import React, { useCallback, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { permissions } from '@picsio/db/src/constants';
import { Spinner } from '@picsio/ui';

import picsioConfig from '../../../../../../config';
import localization from '../../../shared/strings';
import {
  filterItemsByScope,
  filterItemsByMappedTitles,
  filterMetaDiffByTitles,
  generateGroups,
  getMappedTitlesFromDocument,
  GROUP_TITLES_BY_SCOPE,
} from '../../../store/helpers/productAttributes';
import { mergeMultiEnumValue } from '../../../shared/helpers/mergeMultiEnumValue';
import { changeProductCustomField } from '../../../store/products/changeProductCustomField';
import { changeVariantCustomField } from '../../../store/products/changeVariantCustomField';
import resolveSalesChannelId from '../../../store/products/helpers/resolveSalesChannelId';
import { getSavedSalesChannelMapping } from '../../../store/salesChannels';
import { selectSavedSalesChannelMappingState } from '../../../store/selectors/salesChannelMappingSelectors';
import DetailsBlock from '../../DetailsBlock';
import Tooltip from '../../Tooltip/Tooltip';
import ProductCustomFieldGroup from './ProductCustomFieldGroup';
import styles from './ProductMetaFields.module.scss';
import productInfoStyles from '../styles.module.scss';

const ATTRIBUTE_SCOPES = ['product', 'variant'];

const ProductMetaFields = ({
  productId,
  variantId,
  listingVariantId,
  meta,
  metaDiff,
  layer,
  listingId,
  channelId,
  readOnly,
  readOnlyTooltip,
  isBusy,
  attributeScope,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
}) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.productAttributes.items);
  const teamSalesChannels = useSelector((state) => state.user?.team?.salesChannels || []);
  const canManageCustomFields = useSelector(
    (state) => Boolean(state.user?.role?.permissions?.[permissions.manageProductCustomFields]),
  );

  const salesChannelId = useMemo(
    () => (layer === 'listing' ? resolveSalesChannelId(channelId, teamSalesChannels) : null),
    [channelId, layer, teamSalesChannels],
  );

  const mappingState = useSelector(
    (state) => selectSavedSalesChannelMappingState(state, salesChannelId),
  );

  useEffect(() => {
    if (layer !== 'listing' || !salesChannelId) return;
    if (mappingState.status !== 'idle') return;

    dispatch(getSavedSalesChannelMapping({ salesChannelId }));
  }, [dispatch, layer, mappingState.status, salesChannelId]);

  const mappedTitles = useMemo(() => {
    if (layer !== 'listing') return null;
    return getMappedTitlesFromDocument(mappingState.document, attributeScope);
  }, [attributeScope, layer, mappingState.document]);

  const visibleGroups = useMemo(() => {
    const scopeItems = filterItemsByScope(
      items.filter((item) => !item.isTmp),
      attributeScope,
    );
    if (!scopeItems.length) return [];

    if (layer === 'master') {
      return generateGroups(scopeItems, GROUP_TITLES_BY_SCOPE[attributeScope]);
    }

    if (!mappedTitles?.size) return [];

    const mappedItems = filterItemsByMappedTitles(scopeItems, mappedTitles);
    if (!mappedItems.length) return [];

    return generateGroups(mappedItems, GROUP_TITLES_BY_SCOPE[attributeScope]);
  }, [attributeScope, items, layer, mappedTitles]);

  const effectiveMetaDiff = useMemo(() => {
    if (layer !== 'listing' || !mappedTitles?.size) return metaDiff || {};
    return filterMetaDiffByTitles(metaDiff, mappedTitles);
  }, [layer, mappedTitles, metaDiff]);

  const handleCustomFieldChange = useCallback(({
    title, value, type, multiple, isAttach,
  }) => {
    if (readOnly || isBusy || !canManageCustomFields) return;

    let valueToSave = value;
    if (type === 'enum' && multiple && typeof isAttach === 'boolean') {
      valueToSave = mergeMultiEnumValue(meta?.[title], value, isAttach);
    }

    if (attributeScope === 'variant') {
      dispatch(changeVariantCustomField({
        productId,
        variantId,
        layer,
        listingId,
        listingVariantId,
        channelId,
        title,
        value: valueToSave,
      }));
      return;
    }

    dispatch(changeProductCustomField({
      productId,
      layer,
      listingId,
      channelId,
      title,
      value: valueToSave,
    }));
  }, [
    attributeScope,
    canManageCustomFields,
    channelId,
    dispatch,
    isBusy,
    layer,
    listingId,
    listingVariantId,
    meta,
    productId,
    readOnly,
    variantId,
  ]);

  if (!picsioConfig.isPim) return null;

  if (layer === 'listing') {
    if (!channelId || !salesChannelId) return null;
    if (mappingState.status === 'loading' || mappingState.status === 'idle') {
      return (
        <DetailsBlock
          dataQa="productCustomFields"
          detailsPanelVisibility={detailsPanelVisibility}
          toggleVisibility={toggleVisibility}
          blockName={blockName}
          blockTitle={localization.PRODUCT_INFO.customAttributes}
          openByDefault
        >
          <Spinner text="Loading data..." />
        </DetailsBlock>
      );
    }
    if (mappingState.status === 'error') return null;
    if (!mappedTitles?.size) return null;
  }

  if (visibleGroups.length === 0) return null;

  const hasAnyDiff = Object.values(effectiveMetaDiff || {}).some(Boolean);

  return (
    <DetailsBlock
      dataQa="productCustomFields"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={<Tooltip content={hasAnyDiff ? 'Unpublished changes' : null} placement="top"><span className={hasAnyDiff ? productInfoStyles.diffDot : undefined}>{localization.PRODUCT_INFO.customAttributes}</span></Tooltip>}
    >
      <div className={styles.groups}>
        {visibleGroups.map((group) => (
          <ProductCustomFieldGroup
            key={group[0]?.title || group[0]?.id}
            group={group}
            data={meta || {}}
            metaDiff={effectiveMetaDiff}
            detailsPanelVisibility={detailsPanelVisibility}
            toggleVisibility={toggleVisibility}
            readOnly={readOnly || !canManageCustomFields}
            readOnlyTooltip={readOnly ? readOnlyTooltip : null}
            disabled={readOnly || isBusy || !canManageCustomFields}
            onChange={handleCustomFieldChange}
          />
        ))}
      </div>
    </DetailsBlock>
  );
};

ProductMetaFields.propTypes = {
  productId: PropTypes.string.isRequired,
  variantId: PropTypes.string,
  listingVariantId: PropTypes.string,
  meta: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ])),
  metaDiff: PropTypes.objectOf(PropTypes.bool),
  layer: PropTypes.oneOf(['master', 'listing']).isRequired,
  listingId: PropTypes.string,
  channelId: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  isBusy: PropTypes.bool,
  attributeScope: PropTypes.oneOf(ATTRIBUTE_SCOPES).isRequired,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
};

ProductMetaFields.defaultProps = {
  variantId: null,
  listingVariantId: null,
  meta: {},
  metaDiff: {},
  listingId: null,
  channelId: null,
  readOnly: false,
  readOnlyTooltip: null,
  isBusy: false,
};

export default ProductMetaFields;
